import { useMemo } from "react";
import { colors, css, fonts } from "../../Styles";

// #region | NumberDisplay Component
interface numberDisplay_Props {
  header?: string;
  headerColor?: string;
  value: number | string;
  valueColor: string;
  unit: string | JSX.Element;
}
/**
 * NumberDisplay
 * @param header header to display above the single numberDisplay component.
 * @param value the value of the number to be displayed.
 * @param unit the unit of the value.
 * @returns a nice way to display a value/number.
 */
export default function NumberDisplay({
  header,
  value,
  unit,
  headerColor,
  valueColor,
}: numberDisplay_Props) {
  const _headerColor = headerColor ? headerColor : colors.greyDarkPrimary;
  const _valueColor = valueColor ? valueColor : colors.greyDarkPrimary;

  return (
    <div style={{ height: "fit-content", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between" }}>
      <p style={{ ...fonts.header, ...fonts.fontSize3, color: _headerColor }}>{header}</p>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <p style={{ ...fonts.header, ...fonts.fontSize1, color: _valueColor }}>{value}</p>
        <div style={{ width: "20px", height: "1px", backgroundColor: _headerColor }}/>
        <div style={{ ...css.unitsFontstyle, height: "15px", display: "flex", alignItems: "center", marginTop: "5px" }}>{unit}</div>
      </div>
    </div>
  );
}
// ---------------------------------------------------
//#endregion

// #region | NumberDisplayList Component
interface NumberDisplayListProps {
  header?: string;
  headerColor?: string;
  headers?: string[];
  headersColor?: string;
  values: (number | string)[];
  valuesColor?: string;
  units: (string | JSX.Element)[];
  gridRows?: string;
  gridColumns?: string;
  gridGap?: string;
  height?: string;
}
/**
 * NumberDisplayList
 * @param header header to display above the entire list of NumberDisplay components.
 * @param headers array of headers to display one header above every single NumberDisplay component.
 * @param values array of values to be displayed.
 * @param units array of units - defines the amount of NumberDisplay components to be displayed in the list.
 * @returns a nice way to display a list of values/numbers.
 */
export function NumberDisplayList({ header, headers = [], values = [], units, gridRows, gridColumns, headerColor, headersColor, valuesColor, gridGap, height }: NumberDisplayListProps) {
  const _gridColumns = useMemo(() => gridColumns || "1fr 1fr", [gridColumns]);
  const _gridRows = useMemo(() => gridRows || "1fr 1fr", [gridRows]);
  const _gridGap = useMemo(() => gridGap || "10px 20px", [gridGap]);
  const _headerColor = headerColor ? headerColor : colors.greyDarkPrimary;
  const _headersColor = headersColor ? headersColor : colors.greyDarkPrimary;
  const _valuesColor = valuesColor ? valuesColor : colors.greyDarkPrimary;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px", height: height ? height : "170px" }}>
      {header && ( <p style={{ ...fonts.header, ...fonts.fontSize3, color: _headerColor }}>{header}</p>)}
      <div style={{ display: "grid", gridTemplateColumns: _gridColumns, gridTemplateRows: _gridRows, height: "100%", textAlign: "center", gap: _gridGap }}>
        {units.map((unit, index) => (
          <NumberDisplay
            key={index}
            header={headers[index] ?? ""} // Default header to empty string if header is not defined
            value={values[index] ?? 0} // Default value to 0 if values is not defined
            unit={unit}
            headerColor={_headersColor}
            valueColor={_valuesColor}
          />
        ))}
      </div>
    </div>
  );
}
// ---------------------------------------------------
//#endregion
